import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet-routing-machine';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import { Navigation, MapPin, List, Map as MapIcon, ChevronLeft, ChevronRight, Loader2, Layers, Calendar } from 'lucide-react';

// --- STYLES & ICONS ---
const DefaultIcon = L.icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41]
});

const DriverIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41]
});

const GoogleMapsIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48">
    <path fill="#1A73E8" d="M24 4c-7.7 0-14 6.3-14 14 0 10.5 14 26 14 26s14-15.5 14-26c0-7.7-6.3-14-14-14z" />
    <circle fill="#fff" cx="24" cy="18" r="5" />
  </svg>
);

const FollowDriver = ({ position }) => {
  const map = useMap();
  const hasCentered = useRef(false);

  useEffect(() => {
    if (position && !hasCentered.current) {
      map.setView(position, 15);
      hasCentered.current = true;
    }
  }, [position, map]);

  return null;
};

const createStopIcon = (stopNumber) =>
  L.divIcon({
    html: `
      <div style="
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
      ">
        <!-- STOP LABEL -->
        <div style="
          min-width: 44px;
          height: 34px;
          padding: 0 10px;
          background: linear-gradient(135deg, #14b8a6, #0d9488);
          color: #ffffff;
          font-size: 12px;
          font-weight: 700;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
          letter-spacing: 0.5px;
          white-space: nowrap;
        ">
          STOP ${stopNumber}
        </div>

        <!-- ARROW POINTER -->
        <div style="
          width: 0;
          height: 0;
          margin-top: -1px;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid #0d9488;
        "></div>
      </div>
    `,
    className: '',
    iconSize: [48, 42],
    iconAnchor: [24, 42],
  });

// --- STABLE ROUTING COMPONENT ---
const RoutingEngine = ({ userLoc, targetLoc }) => {
  const map = useMap();
  const routingControlRef = useRef(null);
  useEffect(() => {
    if (!map) return;
    if (!routingControlRef.current) {
      routingControlRef.current = L.Routing.control({
        waypoints: [],
        lineOptions: { styles: [{ color: '#00ffcc', weight: 6, opacity: 0.9 }] }, // Brighter teal for satellite visibility
        addWaypoints: false,
        draggableWaypoints: false,
        fitSelectedRoutes: true,
        show: false,
      }).addTo(map);
    }

    if (userLoc && targetLoc) {
      routingControlRef.current.setWaypoints([
        L.latLng(userLoc[0], userLoc[1]),
        L.latLng(targetLoc[0], targetLoc[1])
      ]);
    }

    return () => {
      if (routingControlRef.current && map) {
        try { routingControlRef.current.setWaypoints([]); } catch (e) { }
      }
    };
  }, [map, userLoc, targetLoc]);

  return null;
};

const DriverMap = ({ orders, driverName }) => {
  const [mapOrders, setMapOrders] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeRoute, setActiveRoute] = useState(null);
  const [mobileView, setMobileView] = useState('list');
  const [isSatellite, setIsSatellite] = useState(false); // Satellite Toggle State
  const mapInstanceRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const openInGoogleMaps = (lat, lng) => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    const url = isMobile
      ? `google.navigation:q=${lat},${lng}`
      : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

    window.open(url, '_blank');
  };
  useEffect(() => {
    let watchId = null;
    let intervalId = null;

    const requestLocationPermission = async () => {
      if (!navigator.geolocation) {
        console.error('Geolocation not supported');
        return;
      }
      console.log('Requesting location permission...');
      // Ask permission immediately (Google Maps behavior)
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          console.log('Location permission granted:', latitude, longitude);
          setUserLocation([latitude, longitude]);

          // 🔄 Continuous tracking (movement)
          watchId = navigator.geolocation.watchPosition(
            (position) => {
              setUserLocation([
                position.coords.latitude,
                position.coords.longitude,
              ]);
            },
            (error) => {
              console.error('Watch error:', error);
            },
            { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
          );

          // ⏱ Force update every 1 minute (even if user is idle)
          intervalId = setInterval(() => {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                setUserLocation([
                  position.coords.latitude,
                  position.coords.longitude,
                ]);
              },
              (error) => {
                console.error('Interval error:', error);
              },
              { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
            );
          }, 60 * 1000); // 1 minute
        },
        (error) => {
          console.error('Permission denied or error:', error);
          alert(
            'Location access is required to track deliveries. Please enable location permission.'
          );
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
      );
    };

    requestLocationPermission();

    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);
  // 1. GPS Tracking
  // useEffect(() => {
  //   const watchId = navigator.geolocation.watchPosition(
  //     (pos) => setUserLocation([pos.coords.latitude, pos.coords.longitude]),
  //     (err) => console.error(err),
  //     { enableHighAccuracy: true }
  //   );
  //   return () => navigator.geolocation.clearWatch(watchId);
  // }, []);

  // 2. Geocoding Logic
  useEffect(() => {
    const processOrders = async () => {
      if (!orders || orders.length === 0) return;
      setLoading(true);
      const results = [];

      for (const order of orders) {
        const address = order.delivery_address || order.pickup_address;
        if (!address) continue;

        try {
          await new Promise(r => setTimeout(r, 800));
          const res = await fetch(`${backendUrl}/geocode?q=${encodeURIComponent(address)}`);
          const data = await res.json();

          if (data && data[0]) {
            const lat = parseFloat(data[0].lat);
            const lng = parseFloat(data[0].lon);
            let dist = 0;

            if (userLocation) {
              const R = 6371;
              const dLat = (lat - userLocation[0]) * Math.PI / 180;
              const dLon = (lng - userLocation[1]) * Math.PI / 180;
              const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(userLocation[0] * Math.PI / 180) * Math.cos(lat * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
              dist = R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
            }
            results.push({ ...order, lat, lng, distance: dist });
          }
        } catch (e) { console.error(e); }
      }
      setMapOrders(results.sort((a, b) => a.distance - b.distance));
      setLoading(false);
    };
    processOrders();
  }, [orders, userLocation === null]);

  // 3. Pagination Logic
  const totalPages = Math.ceil(mapOrders.length / itemsPerPage);
  const currentOrders = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return mapOrders.slice(start, start + itemsPerPage);
  }, [mapOrders, currentPage]);

  const handleNavigate = (order) => {
    setActiveRoute([order.lat, order.lng]);
    setMobileView('map');
    setTimeout(() => {
      mapInstanceRef.current?.flyTo([order.lat, order.lng], 15);
    }, 100);
  };

  return (
    <div className="flex flex-col md:flex-row h-[80vh] w-full border rounded-2xl overflow-hidden shadow-2xl bg-slate-50 relative">

      {/* MOBILE TAB TOGGLE */}
      <div className="md:hidden flex bg-white border-b sticky top-0 z-[1002]">
        <button
          onClick={() => setMobileView('list')}
          className={`flex-1 py-3 flex items-center justify-center gap-2 font-semibold text-sm ${mobileView === 'list' ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50' : 'text-slate-500'}`}
        >
          <List size={18} /> List View
        </button>
        <button
          onClick={() => setMobileView('map')}
          className={`flex-1 py-3 flex items-center justify-center gap-2 font-semibold text-sm ${mobileView === 'map' ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50' : 'text-slate-500'}`}
        >
          <MapIcon size={18} /> Map View
        </button>
      </div>

      {/* SIDEBAR */}
      <div className={`${mobileView === 'list' ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-[380px] h-full bg-white z-[1001] shadow-xl`}>
        <div className="p-5 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Navigation size={22} /> Delivery Route
          </h2>
          <p className="text-teal-100 text-xs mt-1">Found {mapOrders.length} optimized stops</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading && mapOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <Loader2 className="animate-spin mb-2" size={32} />
              <p className="text-sm font-medium">Calculating optimal route...</p>
            </div>
          ) : currentOrders.map((order, idx) => (
            <div
              key={order.id}
              className={`group p-4 rounded-xl border-2 transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer
                ${activeRoute?.[0] === order.lat ? 'border-teal-500 bg-teal-50' : 'border-slate-100 bg-white hover:border-teal-200'}`}
              onClick={() => mapInstanceRef.current?.flyTo([order.lat, order.lng], 14)}
            >
              <div className="flex justify-between items-start mb-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-teal-600 bg-teal-100 px-2 py-0.5 rounded">
                  Stop {(currentPage - 1) * itemsPerPage + idx + 1}
                </span>
                <span className="text-sm font-bold text-slate-700 flex items-center gap-1">
                  <Navigation size={12} className="text-teal-500" />
                  {order.distance ? `${order.distance.toFixed(1)} km` : '...'}
                </span>
              </div>
              <h3 className="font-bold text-slate-800 leading-tight mb-1">{order.customer_name}</h3>
              <p className="text-xs text-slate-500 flex items-start gap-1">
                <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                {order.delivery_address}
              </p>
              {/* delevery date  we need to show to the use */}
              <p className="text-xs text-slate-500 flex items-start gap-1">
                <Calendar size={12} className="mt-0.5 flex-shrink-0" />
                {order.delivery_date}
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openInGoogleMaps(order.lat, order.lng);
                }}
                className="mt-4 w-full bg-white border border-slate-200 text-slate-800 text-xs font-bold py-2.5 rounded-lg
             flex items-center justify-center gap-2 hover:bg-slate-100 transition-all"
              >
                <GoogleMapsIcon size={16} />
                Open in Google Maps
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleNavigate(order); }}
                className="mt-4 w-full bg-slate-900 text-white text-xs font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 group-hover:bg-teal-600 transition-colors"
              >
                Start Navigation
              </button>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="p-4 border-t bg-slate-50 flex items-center justify-between">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              className="p-2 rounded-full hover:bg-white disabled:opacity-30 border transition-all"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
              Page {currentPage} of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
              className="p-2 rounded-full hover:bg-white disabled:opacity-30 border transition-all"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>

      {/* MAP VIEW */}
      <div className={`${mobileView === 'map' ? 'block' : 'hidden'} md:block flex-1 h-full relative`}>

        {/* SATELLITE TOGGLE BUTTON */}
        <button
          onClick={() => setIsSatellite(!isSatellite)}
          className="absolute top-4 right-4 z-[1000] bg-white border border-slate-200 p-2 rounded-lg shadow-lg hover:bg-slate-50 transition-colors flex items-center gap-2 text-xs font-bold text-slate-700"
        >
          <Layers size={16} className={isSatellite ? "text-teal-600" : "text-slate-400"} />
          {isSatellite ? "Street View" : "Satellite View"}
        </button>

        <MapContainer
          center={[-25.3444, 131.0369]}
          zoom={7}
          style={{ height: '100%', width: '100%' }}
          whenCreated={(map) => { mapInstanceRef.current = map; }}
        >
          {/* CONDITIONALLY RENDER TILE THEME */}
          {isSatellite ? (
            <TileLayer
              attribution='&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
          ) : (
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          )}
          {userLocation && <FollowDriver position={userLocation} />}
          {userLocation && (
            <Marker position={userLocation} icon={DriverIcon} zIndexOffset={1000}>
              <Popup className="custom-popup">You are here {driverName}</Popup>
            </Marker>
          )}
          {!userLocation && (
            <div className="absolute inset-0 z-[2000] bg-white/10 flex items-center justify-center">
              <p className="text-sm font-bold text-slate-600">
                Waiting for location permission…
              </p>
            </div>
          )}
          {mapOrders.map((order, index) => (
            <Marker key={order.id} position={[order.lat, order.lng]} icon={createStopIcon(index + 1)} zIndexOffset={1000}>

              <Popup>
                <div className="p-2 text-center">
                  <p className="font-bold text-slate-800 mb-2">{order.customer_name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                    {order.delivery_address}
                  </p>
                  <button
                    onClick={() => handleNavigate(order)}
                    className="bg-teal-600 text-white px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  >
                    Go Now
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}

          <RoutingEngine userLoc={userLocation} targetLoc={activeRoute} />
        </MapContainer>

        {loading && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] bg-white/90 backdrop-blur px-4 py-2 rounded-full shadow-lg border border-teal-100 flex items-center gap-2">
            <Loader2 className="animate-spin text-teal-600" size={16} />
            <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Updating Markers</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverMap;